/**
 * Formats a number as a US dollar string with no decimal places (e.g. 1234.56 → "$1,235").
 * Returns a dash when the value is null or undefined.
 */
export const formatCurrency = (value: number | null | undefined): string =>
  value != null
    ? value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
    : '-';

export type ApprovalStatus = 'Pending' | 'Approved' | 'Denied';

/**
 * Normalizes a status string from the API into a consistent title-cased value.
 * The API may return statuses in any casing (e.g. 'approved', 'APPROVED', 'Approved').
 * This ensures all consumers receive one of three canonical values.
 * Unrecognized or missing values default to 'Pending'.
 */
export const normalizeStatus = (status: string | undefined | null): ApprovalStatus => {
  switch (status?.toLowerCase()) {
    case 'approved': return 'Approved';
    case 'denied': return 'Denied';
    default: return 'Pending';
  }
};

/**
 * Formats a Date as a short US locale string (e.g. "Apr 21, 2026").
 * Useful for displaying submission window dates or timestamps in a readable form.
 */
export const formatWindowDate = (date: Date): string =>
  date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

/**
 * Combines a note's system prefix and body into a single display string.
 * Format: "prefix | body" when both are present, or whichever part exists alone.
 */
export const formatNoteText = (
  systemPrefix: string | null | undefined,
  body: string | null | undefined
): string => {
  const p = systemPrefix?.trim() ?? '';
  const b = body?.trim() ?? '';
  if (p && b) return `${p} | ${b}`;
  return p || b;
};

/**
 * Formats a partner name for display. When an English alias exists, returns "Alias (Original)"; otherwise returns the original name unchanged. 
 * Null/undefined names pass through so callers can apply their own fallback (e.g. '---').
 */
export const formatPartnerDisplayName = (
  name: string | null | undefined,
  alias: string | null | undefined,
): string | null | undefined => {
  if (name == null) return name;
  const a = alias?.trim();
  return a ? `${a} (${name})` : name;
};

/**
 * Heuristic: true when a partner name contains characters outside the basic ASCII range.
 * (e.g. Chinese, Japanese, Korean, or accented Latin like Norwegian æ/ø/å)
 */
export const isNonEnglishName = (name: string | null | undefined): boolean =>
  name != null && /[^\u0000-\u007F]/.test(name);

const PACIFIC_TIME_ZONE = 'America/Los_Angeles';

/**
 * Formats an ISO date string (or timestamp) as a long-form Pacific-time date
 * (e.g. "July 15, 2026"). Returns an em-dash for null/undefined/invalid input.
 */
export const formatPacificLongDate = (
  value: string | number | Date | null | undefined,
): string => {
  if (value === null || value === undefined || value === '') return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: PACIFIC_TIME_ZONE,
  });
};

/**
 * Returns the Pacific time-zone abbreviation (e.g. "PDT" in summer, "PST" in
 * winter) for the given instant — defaults to now. Used to label date columns
 * once in the header (e.g. "Date (PDT)") instead of repeating it in every cell.
 */
export const getPacificTimeZoneAbbr = (
  value: string | number | Date = new Date(),
): string => {
  const d = value instanceof Date ? value : new Date(value);
  const safe = Number.isNaN(d.getTime()) ? new Date() : d;
  return (
    new Intl.DateTimeFormat('en-US', {
      timeZone: PACIFIC_TIME_ZONE,
      timeZoneName: 'short',
    })
      .formatToParts(safe)
      .find((part) => part.type === 'timeZoneName')?.value ?? 'PT'
  );
};

/**
 * Formats a timestamp in Pacific time for the two-line date cells used across
 * audit tables (Change history, PDM history). Returns `{ date, time, zone }`
 * where `date` is "MM/DD/YYYY", `time` is a clean "hh:mm AM/PM" (no zone
 * suffix, so callers can place the zone label in the column header instead),
 * and `zone` is the abbreviation derived dynamically for the given date.
 * Returns dashes for invalid input.
 */
export const formatPacificDateTimeParts = (
  value: string | number | Date | null | undefined,
): { date: string; time: string; zone: string } => {
  if (value === null || value === undefined || value === '') {
    return { date: '—', time: '', zone: '' };
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) {
    return { date: '—', time: '', zone: '' };
  }
  const date = d.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    timeZone: PACIFIC_TIME_ZONE,
  });
  const time = d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: PACIFIC_TIME_ZONE,
  });
  return { date, time, zone: getPacificTimeZoneAbbr(d) };
};
